// Releases / Chromium catalog page.
//
// Shows every chromium build we publish, grouped by major. The "active" build
// in each group is whatever `manifestMajor.latestBuild` resolves to — that's
// what veil-update.sh installs on every worker.
//
// This page intentionally only renders chromium binaries; the matching
// profile releases live on /releases/profiles. Both surfaces share the same
// manifest endpoint so they always agree on what is published.

import { compareSemver } from "@veil/shared/version";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import Globe from "lucide-react/dist/esm/icons/globe";
import Plus from "lucide-react/dist/esm/icons/plus";
import { useMemo } from "react";
import { Link } from "wouter";
import { CatalogFreshnessStrip } from "@/components/chromium/catalog-freshness-strip";
import { EmptyState, QueryError } from "@/components/query-state";
import { hasRole, useAuth } from "@/lib/auth-context";
import {
	useChromiumUpstream,
	useFleetStatus,
	useManifest,
} from "@/lib/releases/hooks.ts";

export function ChromiumReleases() {
	const { user } = useAuth();
	const canPublish = hasRole(user, "operator");

	const manifestQuery = useManifest();
	const fleetQuery = useFleetStatus();
	const upstreamQuery = useChromiumUpstream();

	const grouped = useMemo(() => {
		const majors = manifestQuery.data?.manifest?.browserMajors ?? {};
		return Object.entries(majors)
			.map(([majorStr, m]) => {
				const versions = Object.entries(m.builds)
					.map(([version, entry]) => ({ version, ...entry }))
					.sort((a, b) => compareSemver(b.version, a.version));
				return {
					major: Number.parseInt(majorStr, 10),
					latestBuild: m.latestBuild,
					builds: versions,
				};
			})
			.sort((a, b) => b.major - a.major);
	}, [manifestQuery.data]);

	// hostnameByBuild["147"]["147.0.7727.139"] → ["c1","c5"]
	const hostsByBuild = useMemo(() => {
		const out = new Map<string, string[]>();
		for (const h of fleetQuery.data?.hosts ?? []) {
			const installed = h.snapshot?.installedBuilds;
			if (!installed) continue;
			for (const [major, versions] of Object.entries(installed)) {
				for (const v of versions) {
					const key = `${major}:${v}`;
					if (!out.has(key)) out.set(key, []);
					const arr = out.get(key);
					if (arr && !arr.includes(h.hostname)) arr.push(h.hostname);
				}
			}
		}
		return out;
	}, [fleetQuery.data]);

	return (
		<div className="space-y-6">
			<div className="flex items-end justify-between gap-3">
				<div>
					<div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
						<Link href="/releases" className="hover:underline">
							Releases
						</Link>
						<span>›</span>
						<span>Chromium</span>
					</div>
					<h1 className="text-2xl font-semibold tracking-tight">Chromium</h1>
					<p className="text-sm text-muted-foreground">
						Patched Chromium binaries grouped by major version. Workers install
						the latest build per major when they boot, when they are deployed,
						and when you run Sync now on a worker.
					</p>
				</div>
				{canPublish ? (
					<Button asChild>
						<Link href="/releases/new/chromium">
							<Plus className="mr-2 size-4" /> Publish chromium
						</Link>
					</Button>
				) : null}
			</div>

			<CatalogFreshnessStrip
				releases={upstreamQuery.data?.releases ?? []}
				syncedAt={upstreamQuery.data?.syncedAt ?? null}
				loading={upstreamQuery.isPending}
				canSync={canPublish}
			/>

			{manifestQuery.isPending ? <Skeleton className="h-48" /> : null}

			{manifestQuery.isError ? (
				<QueryError
					title="Couldn't load the Chromium catalog"
					error={manifestQuery.error}
					onRetry={() => void manifestQuery.refetch()}
					retrying={manifestQuery.isFetching}
				/>
			) : null}

			{!manifestQuery.isPending &&
			!manifestQuery.isError &&
			grouped.length === 0 ? (
				<EmptyState
					icon={Globe}
					title="No Chromium builds published"
					description="Publish a build to make it installable by every worker on its next sync."
					action={
						canPublish ? (
							<Button asChild size="sm">
								<Link href="/releases/new/chromium">Publish chromium</Link>
							</Button>
						) : null
					}
				/>
			) : null}

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				{grouped.map(({ major, latestBuild, builds }) => (
					<Card key={major} className="hover:bg-muted/20 transition-colors">
						<CardHeader>
							<div className="flex items-center justify-between gap-2">
								<CardTitle className="flex items-center gap-2 text-base">
									<Globe className="size-4" />
									Chromium v{major}
									{latestBuild ? (
										<Badge variant="success">latest {latestBuild}</Badge>
									) : (
										<Badge variant="warning">no latest</Badge>
									)}
								</CardTitle>
								<Link
									href={`/releases/chromium/${major}`}
									className="text-xs text-primary hover:underline"
								>
									Open detail →
								</Link>
							</div>
							<CardDescription>
								{builds.length} build{builds.length === 1 ? "" : "s"} published
							</CardDescription>
						</CardHeader>
						<CardContent className="p-0">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Version</TableHead>
										<TableHead>Released</TableHead>
										<TableHead className="hidden md:table-cell">
											Workers
										</TableHead>
										<TableHead className="text-right">Size</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{builds.slice(0, 5).map((b) => {
										const hosts =
											hostsByBuild.get(`${major}:${b.version}`) ?? [];
										const isLatest = b.version === latestBuild;
										return (
											<TableRow key={b.version}>
												<TableCell className="font-mono text-xs">
													{b.version}
													{isLatest ? (
														<Badge variant="success" className="ml-2">
															latest
														</Badge>
													) : null}
												</TableCell>
												<TableCell className="text-xs text-muted-foreground">
													{formatRelative(b.releaseDate)}
												</TableCell>
												<TableCell className="hidden md:table-cell">
													<HostsBadge hosts={hosts} />
												</TableCell>
												<TableCell className="text-right font-mono tabular-nums text-xs">
													{formatBytes(b.size)}
												</TableCell>
											</TableRow>
										);
									})}
									{builds.length > 5 ? (
										<TableRow>
											<TableCell
												colSpan={4}
												className="text-xs text-muted-foreground"
											>
												+ {builds.length - 5} older build
												{builds.length - 5 === 1 ? "" : "s"} ·{" "}
												<Link
													href={`/releases/chromium/${major}`}
													className="text-primary hover:underline"
												>
													view all
												</Link>
											</TableCell>
										</TableRow>
									) : null}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	);
}

function HostsBadge({ hosts }: { hosts: string[] }) {
	if (hosts.length === 0) {
		return <span className="text-xs text-muted-foreground">—</span>;
	}
	return (
		<div className="flex flex-wrap gap-1">
			{hosts.slice(0, 4).map((h) => (
				<Badge key={h} variant="outline" className="font-mono text-[10px]">
					{h}
				</Badge>
			))}
			{hosts.length > 4 ? (
				<Badge variant="outline" className="text-[10px]">
					+{hosts.length - 4}
				</Badge>
			) : null}
		</div>
	);
}
